import React from "react";
import {
  AlertTriangle,
  Shield,
  Phone,
  CheckCircle,
} from "lucide-react";
import { PreventionTips, TrustBadges, WarningSign } from "../constants";

const Safety = () => {
  return (
    <div className="relative bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 pt-28 pb-20 sm:py-24 lg:py-32 overflow-hidden">
      {/* Animated glow effect */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-500/10 via-transparent to-orange-600/5" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-orange-500/20 rounded-full blur-[120px] glow-pulse" />
      </div>

      {/* Subtle grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />

      {/* Dark overlay for better readability */}
      <div className="absolute inset-0 bg-slate-900/60" />

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-orange-500/10 border border-orange-500/20 px-4 py-2 mb-6">
            <Shield className="h-4 w-4 text-orange-400" />
            <span className="text-sm font-medium text-orange-300">
              Proactive Safety Information
            </span>
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
            Safety First,{" "}
            <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">
              Always
            </span>
          </h1>
          <p className="mt-6 text-lg text-slate-300 sm:text-xl max-w-2xl mx-auto leading-relaxed">
            Your safety is our top priority. Learn how to prevent gas-related
            incidents and recognize warning signs before they become
            emergencies.
          </p>
        </div>

        {/* Trust Badges */}
        <div className="mt-12 flex flex-wrap justify-center gap-6 sm:gap-8">
          {TrustBadges.map((badge) => {
            const Icon = badge.icon;
            return (
              <div
                key={badge.text}
                className="flex items-center gap-3 bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-lg px-4 py-3 hover:border-orange-500/50 transition-colors"
              >
                <Icon className="h-6 w-6 text-orange-400" />
                <div>
                  <div className="text-sm font-semibold text-white">
                    {badge.text}
                  </div>
                  <div className="text-xs text-slate-400">{badge.subtext}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Prevention Tips - NOW FIRST - Different Grid */}
        <div className="mt-20">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-orange-500/10 border border-orange-500/20 px-3 py-1 mb-4">
              <CheckCircle className="h-4 w-4 text-orange-400" />
              <span className="text-xs font-medium text-orange-300">
                PREVENTION FIRST
              </span>
            </div>
            <h2 className="text-3xl font-bold text-white sm:text-4xl mb-4">
              Prevention & Best Practices
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              Proactive measures to keep your home and family safe
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PreventionTips.map((tip, index) => {
              const Icon = tip.icon;
              return (
                <div
                  key={tip.title}
                  style={{ animationDelay: `${index * 0.15}s` }}
                  className="relative group overflow-hidden rounded-xl shadow-lg bg-slate-800/90 backdrop-blur-sm border border-slate-700/50 hover:border-orange-500/50 transition-all duration-300 p-6 fade-in-up"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 rounded-lg p-3 bg-orange-500/10">
                      <Icon className="h-8 w-8 text-orange-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-2">
                        {tip.title}
                      </h3>
                      <p className="text-slate-300 text-sm">
                        {tip.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Warning Signs - NOW SECOND - Horizontal Layout */}
        <div className="mt-20">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-orange-500/10 border border-orange-500/20 px-3 py-1 mb-4">
              <AlertTriangle className="h-4 w-4 text-orange-400" />
              <span className="text-xs font-medium text-orange-300">
                STAY ALERT
              </span>
            </div>
            <h2 className="text-3xl font-bold text-white sm:text-4xl mb-4">
              Warning Signs of a Gas Leak
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              Be alert for these indicators that may signal a gas leak
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {WarningSign.map((sign, index) => {
              const Icon = sign.icon;
              return (
                <div
                  key={index}
                  style={{ animationDelay: `${index * 0.1}s` }}
                  className="bg-slate-800/70 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 hover:border-orange-500/50 transition-all duration-300 fade-in-up"
                >
                  <Icon className={`h-10 w-10 ${sign.color} mb-4 mx-auto`} />
                  <p className="text-white text-center text-sm font-medium">
                    {sign.text}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Emergency callout */}
          <div className="mt-8 bg-orange-900/20 border border-orange-500/30 rounded-xl p-6 backdrop-blur-sm">
            <div className="flex items-start gap-4">
              <AlertTriangle className="h-6 w-6 text-orange-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-white font-bold text-lg mb-2">
                  If You Detect a Gas Leak:
                </h3>
                <p className="text-slate-300 mb-3">
                  Evacuate immediately, avoid creating sparks (don't use
                  electrical switches), and once outside, call 911 or your local
                  gas company emergency line.
                </p>
                <p className="text-slate-400 text-sm">
                  For non-emergency gas line inspections and maintenance,
                  contact us at{" "}
                  <a
                    href="tel:9805406064"
                    className="text-orange-400 hover:text-orange-300 font-semibold"
                  >
                    (980) 540-6064
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Safety Commitment - NOW THIRD - Side by Side Layout */}
        <div className="mt-20">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-orange-500/10 border border-orange-500/20 px-3 py-1 mb-4">
              <Shield className="h-4 w-4 text-orange-400" />
              <span className="text-xs font-medium text-orange-300">
                OUR COMMITMENT
              </span>
            </div>
            <h2 className="text-3xl font-bold text-white sm:text-4xl mb-4">
              How We Keep You Safe
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              Professional service you can trust
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 sm:p-10">
              <Shield className="h-12 w-12 text-orange-400 mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">
                Our Safety Standards
              </h3>
              <p className="text-slate-300 leading-relaxed mb-6">
                We are committed to maintaining the highest safety standards in
                the industry. Our technicians are fully certified and regularly
                trained in the latest safety protocols to ensure your peace of
                mind.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span>State-licensed and insured professionals</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span>Compliance with all local and federal codes</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span>Advanced leak detection equipment</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span>Ongoing safety training and certification</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-orange-600 to-orange-800 rounded-2xl p-8 sm:p-10 text-white">
              <h3 className="text-2xl font-bold mb-6">
                Schedule a Safety Inspection
              </h3>
              <p className="mb-6 text-orange-50">
                Regular inspections can identify potential issues before they
                become emergencies. Our comprehensive safety checks include:
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <span>Complete gas line inspection</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <span>Leak detection and testing</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <span>Appliance connection review</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <span>Ventilation assessment</span>
                </li>
              </ul>
              <a
                href="request-appointment"
                className="inline-flex items-center justify-center w-full bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-all"
              >
                Book Inspection Now
              </a>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 text-center">
          <div className="inline-flex flex-col sm:flex-row gap-4">
            <a
              href="tel:9805406064"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 px-8 py-4 text-lg font-semibold text-white shadow-lg hover:shadow-orange-500/50 hover:scale-105 transition-all"
            >
              <Phone className="h-5 w-5" />
              Call (980) 540-6064
            </a>
            {/* <a
              href="request-appointment"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-800 border-2 border-slate-600 px-8 py-4 text-lg font-semibold text-white hover:bg-slate-700 transition-all"
            >
              temp comment
              Schedule Service
            </a> */}
          </div>
          <p className="mt-6 text-sm text-slate-400">
            Licensed & insured • Professional gas line services • Serving the
            community since 2009
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes glowPulse {
          0%, 100% {
            opacity: 0.3;
          }
          50% {
            opacity: 0.5;
          }
        }

        @keyframes shimmer {
          0% {
            background-position: -250% 0;
          }
          100% {
            background-position: 250% 0;
          }
        }
        
        .fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }
        
        .glow-pulse {
          animation: glowPulse 4s ease-in-out infinite;
        }

        .animate-shimmer {
          animation: shimmer 8s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default Safety;
